import React, { useState, useEffect } from 'react';

interface OptimizedImageProps {
    src: string;
    alt: string;
    className?: string;
    style?: React.CSSProperties;
    priority?: boolean;
    aspectRatio?: string;
    objectFit?: 'cover' | 'contain' | 'fill';
    width?: number | string;
    height?: number | string;
    containerBackground?: string;
}

const memoryImageCache = new Set<string>();

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
    src,
    alt,
    className = '',
    style = {},
    priority = false,
    aspectRatio,
    objectFit = 'cover',
    width,
    height,
    containerBackground
}) => {
    const [isLoaded, setIsLoaded] = useState(() => memoryImageCache.has(src));
    const [error, setError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const MAX_RETRIES = 3;

    // Reset state when src changes
    useEffect(() => {
        if (memoryImageCache.has(src)) {
            setIsLoaded(true);
        } else {
            setIsLoaded(false);
        }
        setError(false);
        setRetryCount(0);
    }, [src]);

    const containerStyle: React.CSSProperties = {
        position: 'relative',
        overflow: 'hidden',
        aspectRatio: aspectRatio,
        width: width,
        height: height,
        background: containerBackground || 'transparent',
        ...style
    };

    const imageStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        objectFit: objectFit,
        opacity: isLoaded ? 1 : 0,
        transition: priority ? 'none' : 'opacity 0.2s ease-out',
        display: 'block',
        willChange: 'opacity'
    };

    const handleError = () => {
        if (retryCount < MAX_RETRIES) {
            // Auto retry
            setTimeout(() => {
                setRetryCount(prev => prev + 1);
            }, 1000 * (retryCount + 1)); // Backoff: 1s, 2s, 3s
        } else {
            setError(true);
        }
    };

    return (
        <div className={`optimized-image-container ${className}`} style={containerStyle}>
            {/* Blur/Placeholder effect */}
            {!isLoaded && !error && (
                <div className={`absolute inset-0 bg-slate-200/50 ${priority ? '' : 'animate-pulse'}`} />
            )}

            {error ? (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-100 text-slate-400">
                    <span className="text-[10px]">Failed to load</span>
                </div>
            ) : (
                <img
                    key={`${src}-${retryCount}`}
                    src={src}
                    alt={alt}
                    width={width}
                    height={height}
                    style={imageStyle}
                    loading={priority ? 'eager' : 'lazy'}
                    fetchPriority={priority ? 'high' : 'auto'}
                    decoding={priority ? 'sync' : 'async'}
                    onLoad={() => {
                        memoryImageCache.add(src);
                        setIsLoaded(true);
                    }}
                    onError={handleError}
                />
            )}
        </div>
    );
};
